import { useEffect, useState } from 'react';
import axios from 'axios';
import { X, Loader2, AlertCircle, RefreshCw, Copy } from 'lucide-react';
import { API } from './_helpers';

export default function CalendarSubscribeModal({ onClose }) {
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState('');
  const [copied, setCopied] = useState(null);
  const [rotating, setRotating] = useState(false);

  useEffect(() => {
    axios.get(`${API}/roster/me/calendar`, { withCredentials: true })
      .then(r => setInfo(r.data))
      .catch(e => setErr(e?.response?.data?.detail || 'Could not load calendar URL'));
  }, []);

  const copy = async (label, url) => {
    try { await navigator.clipboard.writeText(url); setCopied(label); setTimeout(() => setCopied(null), 1800); }
    catch { window.prompt('Copy this URL:', url); }
  };

  const rotate = async () => {
    if (!window.confirm('Rotate your calendar token? The old subscription URL will stop working — re-add it on every device.')) return;
    setRotating(true);
    try { const r = await axios.post(`${API}/roster/me/calendar/rotate`, null, { withCredentials: true }); setInfo(r.data); }
    catch (e) { alert(e?.response?.data?.detail || 'Rotate failed'); }
    setRotating(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl max-w-lg w-full p-5 shadow-xl" onClick={e => e.stopPropagation()}
           data-testid="calendar-subscribe-modal">
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3 className="text-lg font-bold" style={{ color: '#0d2644' }}>Send my roster to my phone calendar</h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              Subscribe once — every published shift appears in your calendar app automatically and updates ~hourly.
            </p>
          </div>
          <button onClick={onClose} className="p-1 -mt-1 -mr-1 rounded hover:bg-muted" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>

        {err && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">{err}</div>}
        {!info && !err && <div className="py-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-muted-foreground" /></div>}

        {info && (
          <div className="space-y-4">
            <div>
              <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-1.5">iPhone / Apple Calendar</div>
              <p className="text-xs text-muted-foreground mb-2">Tap the link on your iPhone — iOS will offer &ldquo;Subscribe&rdquo;.</p>
              <div className="flex gap-2">
                <code className="flex-1 text-xs bg-muted/50 px-2.5 py-2 rounded border break-all" data-testid="calendar-webcal-url">{info.webcal_url}</code>
                <a href={info.webcal_url} className="text-xs font-semibold px-3 py-2 rounded-lg bg-[#0d6efd] text-white whitespace-nowrap inline-flex items-center gap-1"
                   data-testid="calendar-open-apple">Open</a>
                <button onClick={() => copy('apple', info.webcal_url)} className="text-xs font-semibold px-3 py-2 rounded-lg border whitespace-nowrap inline-flex items-center gap-1"
                        data-testid="calendar-copy-apple">
                  <Copy className="w-3 h-3" /> {copied === 'apple' ? 'Copied!' : 'Copy'}
                </button>
              </div>
            </div>

            <div>
              <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-1.5">Google Calendar / Outlook / other</div>
              <p className="text-xs text-muted-foreground mb-2">
                In Google Calendar: <strong>Settings → Add calendar → From URL</strong>, then paste:
              </p>
              <div className="flex gap-2">
                <code className="flex-1 text-xs bg-muted/50 px-2.5 py-2 rounded border break-all" data-testid="calendar-https-url">{info.https_url}</code>
                <button onClick={() => copy('https', info.https_url)} className="text-xs font-semibold px-3 py-2 rounded-lg border whitespace-nowrap inline-flex items-center gap-1"
                        data-testid="calendar-copy-https">
                  <Copy className="w-3 h-3" /> {copied === 'https' ? 'Copied!' : 'Copy'}
                </button>
              </div>
              <a href="https://calendar.google.com/calendar/u/0/r/settings/addbyurl"
                 target="_blank" rel="noopener noreferrer"
                 className="text-xs underline text-[#0d6efd] mt-1 inline-block">
                Open Google Calendar &rsaquo;
              </a>
            </div>

            <div className="border-t pt-3 mt-2">
              <div className="text-xs text-muted-foreground flex items-start gap-2">
                <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <div>
                  This URL is <strong>private</strong> — anyone with the link can see your shifts. If you ever share or lose a device, rotate it below.
                </div>
              </div>
              <button onClick={rotate} disabled={rotating} data-testid="calendar-rotate"
                className="mt-2 text-xs font-semibold inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-amber-700 border-amber-300 hover:bg-amber-50 disabled:opacity-50">
                <RefreshCw className={`w-3 h-3 ${rotating ? 'animate-spin' : ''}`} /> Rotate this URL
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
